import React, { useState, useEffect } from 'react';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import usePopupClose from '../Utils/usePopupClose';
import JobSearchSelect from './Shards/CreateEvaluation/JobSearchSelect';
import TalentSearchSelect from './Shards/CreateEvaluation/TalentSearchSelect';
import RoleSearchSelect from './Shards/CreateEvaluation/RoleSearchSelect';

const CreateEvaluation = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  job = null, // { job_id, title, company }
  talent = null, // { talent_id, name, email }
  contextType = null // 'talent' or 'job'
}) => {
  // Log the job and talent objects for debugging
  // console.log('CreateEvaluation - job prop:', job);
  // console.log('CreateEvaluation - talent prop:', talent);
    
  const { user } = useSession();
  
  // Extract IDs for easier reference
  const jobId = job?.job_id || null;
  const talentId = talent?.talent_id || null;

  // Determine initial criteria type based on context
  const getInitialCriteriaType = () => {
    if (contextType === 'candidate') return 'VSSkillset';
    if (contextType === 'job') return 'VSJob';
    if (contextType === 'talent') return 'VSResume';
    return 'VSJob'; // Default
  };
  
  // Determine available criteria types based on context
  const getAvailableCriteriaTypes = () => {
    if (contextType === 'candidate') {
      return ['VSSkillset']; // Only custom criteria for candidates
    } else if (contextType === 'talent') {
      return ['VSResume', 'VSSkillset'];
    } else if (contextType === 'job') {
      return ['VSJob', 'VSSkillset'];
    }
    return ['VSJob', 'VSResume', 'VSSkillset']; // Default: all options
  };

  // Determine available attach options based on context
  const getAvailableAttachOptions = () => {
    if (contextType === 'candidate') {
      return ['talent']; // Candidates always attach to talent (since candidate is a talent)
    } else if (contextType === 'talent') {
      return ['talent', 'nothing'];
    } else if (contextType === 'job') {
      return ['job', 'nothing'];
    }
    return ['job', 'talent', 'nothing']; // Default: all options
  };
  
  // Generate a default evaluation name with formatted timestamp
  const getDefaultEvaluationName = () => {
    const now = new Date();
    const month = now.toLocaleString('en-US', { month: 'short' });
    const day = now.getDate();
    const suffix = getDaySuffix(day);
    const hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, '0');
    
    return `Evaluation ${month} ${day}${suffix}, ${hours}:${minutes}`;
  };
  
  // Helper function to get the day suffix (st, nd, rd, th)
  const getDaySuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  
  // Determine initial attachTo based on context
  const getInitialAttachTo = () => {
    if (contextType === 'candidate') return 'talent';
    if (contextType === 'job') return 'job';
    if (contextType === 'talent') return 'talent';
    return 'nothing'; // Default for no context
  };

  // Form state
  const [formData, setFormData] = useState({
    evaluationName: getDefaultEvaluationName(),
    criteriaType: getInitialCriteriaType(),
    attachTo: getInitialAttachTo(),
    selectedJob: job || null,
    selectedTalent: talent || null,
    selectedRole: null, // For role selection in VSSkillset when attachTo is "nothing"
    isDefaultForJob: false,
    customCriteria: '',
    extraInstructions: '',
    askToUploadResume: false,
    includeVideoIntro: false,
    kycIdentityCheck: false,
    seniority: 'Senior', // Default seniority level
    minQuestionnaireItems: '10', // Default minimum questionnaire items
    minChallengeItems: '10', // Default minimum challenge items
  });
  
  
  // Reset form when popup is opened/closed
  useEffect(() => {
    if (isOpen) {
      setFormData({
        evaluationName: getDefaultEvaluationName(),
        criteriaType: getInitialCriteriaType(),
        attachTo: getInitialAttachTo(),
        selectedJob: job || null,
        selectedTalent: talent || null,
        selectedRole: null, // Reset selected role
        isDefaultForJob: false,
        customCriteria: '',
        extraInstructions: '',
        askToUploadResume: false,
        includeVideoIntro: false,
        kycIdentityCheck: false,
        seniority: 'Senior', // Default seniority level
        minQuestionnaireItems: '10', // Default minimum questionnaire items
        minChallengeItems: '10', // Default minimum challenge items
      });
    }
  }, [isOpen, job, talent]);
  
  // Set attachTo based on criteriaType
  useEffect(() => {
    if (formData.criteriaType === 'VSJob') {
      setFormData(prev => ({ ...prev, attachTo: 'job' }));
    } else if (formData.criteriaType === 'VSResume') {
      setFormData(prev => ({ ...prev, attachTo: 'talent' }));
    }
  }, [formData.criteriaType]);
  
  // Prefill role when criteriaType changes to VSSkillset and we have talent/job data
  useEffect(() => {
    if (formData.criteriaType === 'VSSkillset') {
      if (formData.selectedTalent?.roleCategory && !formData.selectedRole) {
        setFormData(prev => ({
          ...prev,
          selectedRole: {
            subCategory: formData.selectedTalent.roleCategory.subCategory,
            mainCategory: formData.selectedTalent.roleCategory.mainCategory,
            altSubcategory: formData.selectedTalent.roleCategory.altSubcategory
          }
        }));
      } else if (formData.selectedJob?.job_category_json && !formData.selectedRole) {
        setFormData(prev => ({
          ...prev,
          selectedRole: {
            subCategory: formData.selectedJob.job_category_json.subCategory,
            mainCategory: formData.selectedJob.job_category_json.mainCategory,
            altSubcategory: formData.selectedJob.job_category_json.altSubcategory
          }
        }));
      }
    }
  }, [formData.criteriaType, formData.selectedTalent, formData.selectedJob]);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle criteria type change
  const handleCriteriaTypeChange = (e) => {
    const value = e.target.value;
    
    // Update criteria type
    setFormData(prev => ({
      ...prev,
      criteriaType: value,
      // Reset custom criteria if not VSSkillset
      customCriteria: value !== 'VSSkillset' ? '' : prev.customCriteria
    }));
    
    // Force attachTo based on criteria type
    if (value === 'VSJob') {
      setFormData(prev => ({ 
        ...prev, 
        criteriaType: value,
        attachTo: 'job',
        // Reset talent selection
        selectedTalent: null,
        // Always preserve job if provided via props
        selectedJob: job || null,
        isDefaultForJob: false
      }));
    } else if (value === 'VSResume') {
      setFormData(prev => ({ 
        ...prev, 
        criteriaType: value,
        attachTo: 'talent',
        // Reset job selection and default flag
        selectedJob: null,
        // Always preserve talent if provided via props
        selectedTalent: talent || null,
        isDefaultForJob: false
      }));
    } else if (value === 'VSSkillset') {
      // For VSSkillset, set attachTo based on context
      const attachTo = contextType === 'job' ? 'job' : 
                      (contextType === 'talent' || contextType === 'candidate') ? 'talent' : 
                      'nothing';
      
      setFormData(prev => ({
        ...prev,
        attachTo,
        selectedJob: attachTo === 'job' ? (job || null) : null,
        selectedTalent: attachTo === 'talent' ? (talent || null) : null,
        selectedRole: null // Reset selected role
      }));
    }
  };
  
  // Handle attach to change
  const handleAttachToChange = (e) => {
    const value = e.target.value;
    
    setFormData(prev => ({
      ...prev,
      attachTo: value,
      // Reset selections based on new attachTo value
      selectedJob: value !== 'job' ? null : prev.selectedJob,
      selectedTalent: value !== 'talent' ? null : prev.selectedTalent,
      // Reset selectedRole if attachTo is not 'nothing'
      selectedRole: value !== 'nothing' ? null : prev.selectedRole,
      isDefaultForJob: value !== 'job' ? false : prev.isDefaultForJob
    }));
  };
  
  
  // Handle job selection
  const handleJobSelect = (job) => {
    setFormData(prev => ({
      ...prev,
      selectedJob: job,
      // If VSSkillset and job has role category, prefill the role
      selectedRole: (prev.criteriaType === 'VSSkillset' && job?.job_category_json) ? {
        subCategory: job.job_category_json.subCategory,
        mainCategory: job.job_category_json.mainCategory,
        altSubcategory: job.job_category_json.altSubcategory
      } : prev.selectedRole
    }));
  };
  
  // Handle talent selection
  const handleTalentSelect = (talent) => {
    setFormData(prev => ({
      ...prev,
      selectedTalent: talent,
      // If VSSkillset and talent has role category, prefill the role
      selectedRole: (prev.criteriaType === 'VSSkillset' && talent?.roleCategory) ? {
        subCategory: talent.roleCategory.subCategory,
        mainCategory: talent.roleCategory.mainCategory,
        altSubcategory: talent.roleCategory.altSubcategory
      } : prev.selectedRole
    }));
  };
  
  // Clear job selection
  const clearJobSelection = () => {
    setFormData(prev => ({
      ...prev,
      selectedJob: null
    }));
  };
  
  // Clear talent selection
  const clearTalentSelection = () => {
    setFormData(prev => ({
      ...prev,
      selectedTalent: null
    }));
  };
  
  // Handle role selection
  const handleRoleSelect = (role) => {
    setFormData(prev => ({
      ...prev,
      selectedRole: role
    }));
  };
  
  // Clear role selection
  const clearRoleSelection = () => {
    setFormData(prev => ({
      ...prev,
      selectedRole: null
    }));
  };
  
  // State for submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.evaluationName) {
      alert('Please enter an evaluation name');
      return;
    }
    
    if (formData.attachTo === 'job' && !formData.selectedJob) {
      alert('Please select a job');
      return;
    }
    
    if (formData.attachTo === 'talent' && !formData.selectedTalent) {
      alert('Please select a talent');
      return;
    }
    
    if (formData.criteriaType === 'VSSkillset' && !formData.selectedRole) {
      alert('Please select a role');
      return;
    }
    
    if (formData.criteriaType === 'VSSkillset' && !formData.customCriteria) {
      alert('Please enter custom criteria');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }

      // Determine which API endpoint to use based on criteria type
      let endpoint;
      let requestBody = {};
      
      if (formData.criteriaType === 'VSJob') {
        endpoint = API_ENDPOINTS.GENERATE_VSJOB_EVALUATION;
        requestBody = {
          job_id: formData.selectedJob.job_id,
          name: formData.evaluationName,
          is_default: formData.isDefaultForJob,
          extra_instructions: formData.extraInstructions
        };
      } else if (formData.criteriaType === 'VSResume') {
        endpoint = API_ENDPOINTS.GENERATE_VSRESUME_EVALUATION;
        requestBody = {
          talent_id: formData.selectedTalent.talent_id,
          name: formData.evaluationName,
          create_eval: true, // hardcode to always create eval
          is_default: false, // hardcode to always not default
          extra_instructions: formData.extraInstructions
        };
      } else if (formData.criteriaType === 'VSSkillset') {
        endpoint = API_ENDPOINTS.GENERATE_VSSKILLSET_EVALUATION;
        requestBody = {
          name: formData.evaluationName,
          criteria: formData.customCriteria
        };
        
        // Always add role category info if a role is selected
        if (formData.selectedRole) {
          // Create a JSON object with subCategory and mainCategory
          requestBody.role_category = {
            subCategory: formData.selectedRole.subCategory,
            mainCategory: formData.selectedRole.mainCategory
          };
        }
        
        // Always add seniority level and min items for VSSkillset
        requestBody.seniority = formData.seniority;
        requestBody.min_questionnaire_items = parseInt(formData.minQuestionnaireItems);
        requestBody.min_challenge_items = parseInt(formData.minChallengeItems);
        
        // Add job_id or talent_id if attached to job or talent
        if (formData.attachTo === 'job' && formData.selectedJob) {
          requestBody.job_id = formData.selectedJob.job_id;
          requestBody.is_default = formData.isDefaultForJob;
        } else if (formData.attachTo === 'talent' && formData.selectedTalent) {
          requestBody.talent_id = formData.selectedTalent.talent_id;
          requestBody.candidate_id = formData.selectedTalent.candidate_id;
        }
      }
      
      console.log("CreateEvaluation requestBody::: ", requestBody);

      // Make API request
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      // Check if the response is ok
      if (!response.ok) {
        let errorMessage = `Error: ${response.status} ${response.statusText}`;
        
        // Try to get the server error message from response body
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          // If we can't parse the error response, use the default message
          console.log('Could not parse error response:', parseError);
        }
        
        const error = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }
      
      // Parse response
      const data = await response.json();
      
      // Call onSubmit with the response data and form data
      onSubmit({
        ...formData,
        response: data
      });
      
      // Close modal
      onClose();
      
    } catch (err) {
      console.error('Error creating evaluation:', err);
      setSubmitError(err.message || 'Failed to create evaluation');
      
      // Show error alert
      alert(`Failed to create evaluation: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Use the popup close hook to handle ESC key press only (not clicks outside)
  const modalRef = usePopupClose(isOpen, onClose, false, true);
  
  // If modal is not open, don't render anything
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100]">
      {/* Backdrop - removed onClick to prevent closing when clicking outside */}
      <div className="fixed inset-0 bg-black bg-opacity-50"></div>
      
      {/* Modal */}
      <div ref={modalRef} className="relative bg-white p-6 rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Modal header */}
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Create New Evaluation</h3>
        
        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Evaluation Name */}
          <div className="mb-6">
            <label htmlFor="evaluationName" className="block text-base font-medium text-gray-700 mb-2">
              Evaluation Name
            </label>
            <input
              type="text"
              id="evaluationName"
              name="evaluationName"
              value={formData.evaluationName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter evaluation name"
            />
          </div>
          
          {/* Criteria Type */}
          <div className="mb-6">
            <label htmlFor="criteriaType" className="block text-base font-medium text-gray-700 mb-2">
              Select Criteria on which to base the evaluation.
            </label>
            <select
              id="criteriaType"
              name="criteriaType"
              value={formData.criteriaType}
              onChange={handleCriteriaTypeChange}
              className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {getAvailableCriteriaTypes().includes('VSJob') && (
                <option value="VSJob">Job Requirements (VSJob)</option>
              )}
              {getAvailableCriteriaTypes().includes('VSResume') && (
                <option value="VSResume">Talent Resume (VSResume)</option>
              )}
              {getAvailableCriteriaTypes().includes('VSSkillset') && (
                <option value="VSSkillset">Custom (VSSkillset)</option>
              )}
            </select>
          </div>
          
          {/* Attach To - Only show for VSSkillset and when not in a specific context */}
          {formData.criteriaType === 'VSSkillset' && !contextType && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attach evaluation to
              </label>
              <div className="flex space-x-4">
                {getAvailableAttachOptions().includes('job') && (
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="attachTo"
                      value="job"
                      checked={formData.attachTo === 'job'}
                      onChange={handleAttachToChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Job</span>
                  </label>
                )}
                {getAvailableAttachOptions().includes('talent') && (
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="attachTo"
                      value="talent"
                      checked={formData.attachTo === 'talent'}
                      onChange={handleAttachToChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Talent</span>
                  </label>
                )}
                {getAvailableAttachOptions().includes('nothing') && (
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="attachTo"
                      value="nothing"
                      checked={formData.attachTo === 'nothing'}
                      onChange={handleAttachToChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Nothing</span>
                  </label>
                )}
              </div>
            </div>
          )}
          
          {/* Job Search - Show only if attachTo is job */}
          {formData.attachTo === 'job' && (
            <div className="mb-4">
              <JobSearchSelect
                selectedJob={formData.selectedJob}
                onJobSelect={handleJobSelect}
                onClear={clearJobSelection}
                disabled={false}
                clearable={true}
              />
            </div>
          )}
          
          {/* Talent Search - Show only if attachTo is talent */}
          {formData.attachTo === 'talent' && (
            <div className="mb-4">
              <TalentSearchSelect
                selectedTalent={formData.selectedTalent}
                onTalentSelect={handleTalentSelect}
                onClear={clearTalentSelection}
                disabled={false}
                clearable={true}
              />
            </div>
          )}
          
          {/* Default Evaluation Checkbox - Show only if attachTo is job */}
          {formData.attachTo === 'job' && (
            <div className="mb-4">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  name="isDefaultForJob"
                  checked={formData.isDefaultForJob}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Is default evaluation for job?</span>
              </label>
            </div>
          )}
          
          {/* Custom Criteria Textarea - Show only if criteriaType is VSSkillset */}
          {formData.criteriaType === 'VSSkillset' && (
            <>
              {/* Role Search and Seniority - custom width layout */}
              <div className="mb-6 grid gap-4" style={{gridTemplateColumns: '65% 32%'}}>
                <div>
                  <RoleSearchSelect
                    selectedRole={formData.selectedRole}
                    onRoleSelect={handleRoleSelect}
                    onClear={clearRoleSelection}
                  />
                </div>
                <div>
                  <label htmlFor="seniority" className="block text-base font-medium text-gray-700 mb-2">
                    Seniority Level
                  </label>
                  <select
                    id="seniority"
                    name="seniority"
                    value={formData.seniority}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Junior">Junior</option>
                    <option value="Middle">Middle</option>
                    <option value="Senior">Senior</option>
                  </select>
                </div>
              </div>
              
              <div className="mb-6">
                <label htmlFor="customCriteria" className="block text-base font-medium text-gray-700 mb-2">
                  Enter Criteria (Be explicit)
                </label>
                <textarea
                  id="customCriteria"
                  name="customCriteria"
                  value={formData.customCriteria}
                  onChange={handleInputChange}
                  rows={5}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="I want to verify a Mobile Developer for SKILL1, SKILL2 and SKILL3, and understand if they have experience building high-security apps. I would like to ask specifically QUESTION1, QUESTION2 and QUESTION3."
                ></textarea>
              </div>
              
              {/* Min Items - 2 column layout */}
              <div className="mb-6 grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="minQuestionnaireItems" className="block text-base font-medium text-gray-700 mb-2">
                    Min Questionnaire Items
                  </label>
                  <select
                    id="minQuestionnaireItems"
                    name="minQuestionnaireItems"
                    value={formData.minQuestionnaireItems}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="15">15</option>
                    <option value="20">20</option>
                    <option value="25">25</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="minChallengeItems" className="block text-base font-medium text-gray-700 mb-2">
                    Min Challenge Items
                  </label>
                  <select
                    id="minChallengeItems"
                    name="minChallengeItems"
                    value={formData.minChallengeItems}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="15">15</option>
                    <option value="20">20</option>
                    <option value="25">25</option>
                  </select>
                </div>
              </div>
            </>
          )}
          
          {/* Extra Instructions - Only show if NOT using VSSkillset */}
          {formData.criteriaType !== 'VSSkillset' && (
            <div className="mb-6">
              <label htmlFor="extraInstructions" className="block text-base font-medium text-gray-700 mb-2">
                Additional Criteria Instructions
              </label>
              <textarea
                id="extraInstructions"
                name="extraInstructions"
                value={formData.extraInstructions}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter any additional instructions..."
              ></textarea>
            </div>
          )}
          
          {/* Evaluation Configure Steps */}
          <div className="mb-6">
            <label className="block text-base font-medium text-gray-700 mb-2">
              Configure Evaluation Steps (Coming soon)
            </label>
            <div className="flex flex-col space-y-3">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="askToUploadResume"
                  checked={formData.askToUploadResume === true}
                  onChange={handleInputChange}
                  className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={true}
                />
                <span className="ml-2 text-sm text-gray-400">Upload Resume</span>
              </label>
              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="includeVideoIntro"
                  checked={formData.includeVideoIntro}
                  onChange={handleInputChange}
                  className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={true}
                />
                <span className="ml-2 text-sm text-gray-400">60 Second Video Intro</span>
              </label>
              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="kycIdentityCheck"
                  checked={formData.kycIdentityCheck}
                  onChange={handleInputChange}
                  className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={true}
                />
                <span className="ml-2 text-sm text-gray-400">KYC Identity Check</span>
              </label>
            </div>
          </div>
          
          {/* Submit Button */}
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="mr-3 px-6 py-3 border border-gray-300 rounded-lg shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Evaluation'
                )}
              </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEvaluation;
